import { formatCurrency } from "@gymflow/helpers";
import { createColumnHelper } from "@tanstack/react-table";
import { useClubSettings } from "apps/portal/src/providers";
import useGymflowModels from "apps/portal/src/store";
import { useStaffGetInvoiceReceipt } from "libs/api/src/lib/hooks/staff/useStaffGetInvoiceReceipt";
import { DateTime } from "luxon";
import { useTranslation } from "react-i18next";

import { Table } from "../../../organisms/Table";
import { formatTransactionDate } from "../helpers";
import PaymentMethod from "./PaymentMethod";

type TotalRow = {
  label: React.ReactElement;
  date?: React.ReactElement;
  amount: string;
};

type InvoiceTotalsTableProps = {
  invoiceNumber: string;
};

const InvoiceTotalsTable = ({ invoiceNumber }: InvoiceTotalsTableProps) => {
  const { t } = useTranslation();
  const { club } = useClubSettings();
  const { api } = useGymflowModels();

  const { data: invoice } = useStaffGetInvoiceReceipt({
    api,
    invoiceNumber,
  });
  if (!invoice) return null;

  const columnHelper = createColumnHelper<TotalRow>();

  const totalColumns = [
    columnHelper.accessor("label", {
      header: () => t("components.paymentsSidebarProvider.invoice.total"),
      cell: (info) => info.getValue(),
      enableSorting: false,
    }),
    columnHelper.accessor("date", {
      header: () => null,
      cell: (info) => info.getValue(),
      enableSorting: false,
    }),
    columnHelper.accessor("amount", {
      header: () =>
        `${formatCurrency(invoice.totalAmount, club.defaultCurrency)}`,
      cell: (info) => info.getValue(),
      enableSorting: false,
    }),
  ];

  const totalData = invoice.transactions.map((transaction) => {
    const { date: formattedDate, time: formattedTime } = formatTransactionDate(
      transaction.transactionDate,
      DateTime.DATE_MED,
      DateTime.TIME_SIMPLE,
      club.timezone,
    );

    const minusSign =
      transaction.type === "WRITE_OFF" ||
      transaction.type === "REFUND_DEBIT" ||
      transaction.type === "MANUAL_CREDIT"
        ? "-"
        : "";

    return {
      label: <PaymentMethod reportItem={transaction} />,
      date:
        formattedDate && formattedTime ? (
          <div className="flex items-center gap-1 text-gray-500">
            <div>{formattedDate}</div>
            <div className="size-1.5 rounded-full bg-gray-200"></div>
            <div>{formattedTime}</div>
          </div>
        ) : null,
      amount: `${minusSign} ${formatCurrency(transaction.amount, club.defaultCurrency)}`,
    };
  });

  return (
    <div className="flex justify-end">
      <div className="w-[28rem] rounded-b-2xl border-x border-b border-gray-200 bg-gray-50 text-sm">
        <div className="bg-white">
          <Table
            data={totalData}
            columns={totalColumns}
            isFetching={false}
            className="!relative w-full"
            rowClassName="h-9 border-b text-gray-500 text-center [&>td:last-child]:text-right"
            thRowClassName="[&>th:last-child]:text-right"
            thClassName="text-lg font-bold text-gray-950 last:pr-3"
            cellClassName="last:pr-3"
          />
        </div>
        <div className="flex justify-between px-4 py-3 text-lg font-semibold">
          <div>{t("components.paymentsSidebarProvider.invoice.amountDue")}</div>
          <div>{formatCurrency(invoice.dueAmount, club.defaultCurrency)}</div>
        </div>
      </div>
    </div>
  );
};

export default InvoiceTotalsTable;
